/**
 * main.jsx - Application Entry Point
 * 
 * This is the entry point of the React application. It's responsible for:
 * 1. Setting up the React root
 * 2. Configuring the React Router
 * 3. Importing global styles and dependencies
 * 4. Rendering the main App component
 * 
 * Dependencies:
 * - react: Core React library
 * - react-dom: React's DOM rendering utilities
 * - react-router-dom: Client-side routing
 * - bootstrap: CSS framework for styling
 */

// React and React DOM imports for rendering the application
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

// Routing
import { BrowserRouter } from 'react-router-dom';
import { unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';
import { createBrowserHistory } from 'history';

// Bootstrap CSS and JS (includes Popper.js for Bootstrap components)
import 'bootstrap/dist/css/bootstrap.min.css';  // Bootstrap core CSS
import 'bootstrap/dist/js/bootstrap.bundle.min.js'; // Bootstrap JS with Popper

// Global styles
import './index.css'; // Custom global styles

// Main App component
import App from './App.jsx'; // Root component of the application

/**
 * Create a root for the React application and render it
 * 
 * This code:
 * 1. Finds the DOM element with id 'root' where the app will be mounted
 * 2. Creates a React root
 * 3. Renders the application inside StrictMode for additional development checks
 * 4. Wraps the app with BrowserRouter to enable client-side routing
 */
createRoot(document.getElementById('root')).render(
  // StrictMode helps identify potential problems in the application during development
  <StrictMode>
    {/* 
      BrowserRouter enables client-side routing using the HTML5 history API 
      to keep the UI in sync with the URL 
    */}
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      {/* The root App component that contains all routes and layout */}
      <App />
    </BrowserRouter>
  </StrictMode>,
);
